import { AuthUserRoomMembership } from "queries/chat/rooms/types";
import { useMemo } from "react";
import {
  decryptRoomText,
  handleDecryptDEK,
  importRoomDek,
} from "ui/components/chat/helpers/encryption";

interface RoomAccess {
  roomID: string;
  userEncRoomDEKBase64: string;
}

interface MessageData {
  message: {
    roomEncTextBase64?: string;
    text: string;
  };
  [key: string]: any;
}

export const getAuthUserRoomMembershipsByKey = (
  roomMemberships: AuthUserRoomMembership[],
  key: keyof AuthUserRoomMembership,
  value: string
): AuthUserRoomMembership | undefined => {
  if (!key || !value) return undefined;
  return roomMemberships.find((roomAccess) => roomAccess[key] === value);
};

export const getAuthUserRoomMembershipsByRoomID = (
  roomMemberships: AuthUserRoomMembership[],
  roomID: string
): AuthUserRoomMembership | undefined => {
  return getAuthUserRoomMembershipsByKey(roomMemberships, "roomID", roomID);
};

export const decryptSingleMessage = async (
  data: MessageData,
  keys: CryptoKey[]
): Promise<MessageData> => {
  const { roomEncTextBase64 } = data.message;
  if (!roomEncTextBase64) return data;

  for (const key of keys) {
    const text = await decryptRoomText(roomEncTextBase64, key);
    if (text !== null) return { ...data, message: { ...data.message, text } };
  }
  console.error("error in decryptedMessages", { data });
  return data;
};

/**
 * Decrypts with the room's public key first (what iOS sends with in community
 * rooms), then the member's own key.
 */
export const handleDecryptMessages = async (
  encDEKBase64: string,
  kekBase64: string,
  messages: MessageData[],
  publicDEKBase64?: string
): Promise<MessageData[]> => {
  try {
    if (!messages.length) return messages;

    const keys: CryptoKey[] = [];
    const publicKey = publicDEKBase64 ? await importRoomDek(publicDEKBase64) : null;
    if (publicKey) keys.push(publicKey);
    if (encDEKBase64 && kekBase64) {
      const unwrapped = await handleDecryptDEK({
        KEKBase64: kekBase64,
        encDEKBase64,
      }).catch(() => null);
      if (unwrapped instanceof CryptoKey) keys.push(unwrapped);
    }
    if (!keys.length) return messages;

    return await Promise.all(
      messages.map((data) => decryptSingleMessage(data, keys))
    );
  } catch (error) {
    console.error("failed to decrypt messages", error);
    return messages;
  }
};

interface UseChatSecurity {
  handleDecryptMessages: (messages: MessageData[]) => Promise<MessageData[]>;
  getAuthUserRoomMembershipsByRoomID: (
    roomID: string
  ) => AuthUserRoomMembership | undefined;
}

export const useChatSecurity = ({
  roomID,
  roomMemberships,
  encDEKBase64,
  kekBase64,
  publicDEKBase64,
}: {
  roomID: string;
  roomMemberships: AuthUserRoomMembership[];
  encDEKBase64: string;
  kekBase64: string;
  publicDEKBase64?: string;
}): UseChatSecurity => {
  const securityUtils: UseChatSecurity = useMemo(() => {
    return {
      handleDecryptMessages: (messages: MessageData[]) =>
        handleDecryptMessages(
          encDEKBase64,
          kekBase64,
          messages,
          publicDEKBase64
        ),
      getAuthUserRoomMembershipsByRoomID: (roomID: string) =>
        getAuthUserRoomMembershipsByRoomID(roomMemberships, roomID),
    };
  }, [kekBase64, encDEKBase64, publicDEKBase64, roomMemberships, roomID]);

  return securityUtils;
};
